import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import CollapsibleCategoriesList from "./CollapsibleCategoriesList";
import { UserCategoryViewModel } from "../../../../configuration/api/apiSchema";
import { HandymanAppState } from "../../../../configuration/redux/reducers";
import { getUserCategories, updateUserCategoryRate } from "../../../Categories/actions";
import { Snackbar } from "@mui/material";
import { 
    GlassInfoCard, 
    GlassSnackbar, 
    GlassAlert, 
    SectionHeader,
    BodyText 
} from "../../../../components/glassmorphism/GlassmorphismStyles";

/** ========== TYPES ========== */
interface StateFromProps {
  isLoading: boolean;
  isCategoryRateUpdated: boolean;
  userCategories?: UserCategoryViewModel[];
}

interface DispatchFromProps {
  getUserCategories: (userId?: string) => void;
  updateUserCategoryRate: (category: UserCategoryViewModel) => void;
}

interface PassedProps {
  userId?: string;
}

/** 
 * Combined props 
 */
type Props = PassedProps & DispatchFromProps & StateFromProps;

/** ========== COMPONENT ========== */
const UserRates: React.FC<Props> = ({
  isLoading,
  isCategoryRateUpdated,
  userCategories,
  getUserCategories,
  updateUserCategoryRate,
  userId
}) => {
  // Local state to track whether the Snackbar is visible
  const [alertVisible, setAlertVisible] = useState(false);

  useEffect(() => {
    if (userId) {
      getUserCategories(userId);
    }
    else {
      getUserCategories();
    }

  }, [getUserCategories]);

  /**
   * Whenever isCategoryRateUpdated goes true, show the alert.
   * Then, after a re-render, we can reset the Redux flag if we want,
   * or just rely on autoHideDuration to close the alert.
   */
  useEffect(() => {
    if (isCategoryRateUpdated) {
      setAlertVisible(true);
    }
  }, [isCategoryRateUpdated]);

  // Handler for closing the Snackbar
  const handleClose = () => {
    setAlertVisible(false);
  };

  if (isLoading) {
    return (
      <GlassInfoCard>
        <BodyText sx={{ textAlign: 'center', py: 4 }}>
          Loading rates...
        </BodyText>
      </GlassInfoCard>
    );
  }

  return (
    <>
      {/* Alert if the user updated a rate */}
      <GlassSnackbar
        open={alertVisible}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <GlassAlert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
          Rate updated successfully!
        </GlassAlert>
      </GlassSnackbar>

      <GlassInfoCard>
        <SectionHeader sx={{ mb: 3 }}>Service Rates</SectionHeader>
        <BodyText sx={{ mb: 3, fontSize: '14px' }}>
          Set your hourly rates and fixed prices for different service categories.
        </BodyText>
        <CollapsibleCategoriesList
          categories={userCategories || []}
          onRateChange={updateUserCategoryRate}
          userId={userId}
        />
      </GlassInfoCard>
    </>
  );
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
  isLoading: state.category.isLoading,
  userCategories: state.category.userCategories,
  isCategoryRateUpdated: state.category.isCategoryRateUpdated,
});

const mapDispatchToProps: DispatchFromProps = {
  getUserCategories,
  updateUserCategoryRate,
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  PassedProps,
  HandymanAppState
>(
  mapStateToProps,
  mapDispatchToProps
)(UserRates);
